'use client';

import type { FC, ReactNode } from 'react';
import { createContext, useCallback, useContext, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { usePathname } from 'next/navigation';
import type { ScreenName } from '../types/navigation';
import type { TabId } from '../types/tabs';
import { cn } from '../utils/cn';
import { useNavigation } from '../contexts/NavigationContext';
import { useScrollManager } from '../contexts/ScrollManagerContext';
import { TabBar } from '../components/TabBar';
import { useIsDesktop } from '../hooks/useIsDesktop';
import { useRuntime } from '../runtime/RuntimeContext';

/** Tab-root paths whose screens conceptually own the bottom tab bar.
 * The provider derives `tabBar.visible` from the current pathname so
 * the bar is present on the first paint after SPA navigation — no
 * dependency on a child <ScreenTabBar /> effect that fires post-paint.
 * Detail pages (case details, battle details, etc.) don't match →
 * default stays hidden. Screens outside this set that still want the
 * tab bar (e.g. /help/recruiter) opt in by mounting <ScreenTabBar />,
 * which increments an override counter. */
const TAB_ROOT_PATHNAMES: ReadonlySet<string> = new Set([
  '/',
  '/home',
  '/cases',
  '/inventory',
  '/profile',
  '/games',
  '/referrals',
  '/shop',
  '/payment',
]);

function isTabRootPathname(pathname: string | null): boolean {
  if (!pathname) return false;
  if (TAB_ROOT_PATHNAMES.has(pathname)) return true;
  if (pathname.startsWith('/shop/')) return true;
  return false;
}

/** Entrance/exit phase of the fixed-bottom bar.
 *  `none` — no motion class at all (steady state, or motion is off);
 *  `in`   — slide up from below;
 *  `out`  — slide down, content is retained until the animation ends. */
type FixedMotion = 'none' | 'in' | 'out';

/** Must match `animate-shot-fixed-bottom-out` in animations.css. */
const FIXED_BOTTOM_EXIT_MS = 200;

/** A bar mount/unmount landing within this window of the screen element itself
 * changing is a navigation, not a mode toggle. The stack already slides the
 * whole screen sideways; adding a vertical slide would drag the bar diagonally,
 * and screens whose bar is permanent (case-open results, wheel) would replay an
 * entrance on every visit. The follow-up commit lands in the same tick, so the
 * window only has to outlive one frame. */
const SCREEN_SETTLE_MS = 100;

/** Air between the last row of content and the top of the fixed bottom bar, in px.
 *
 * The reserve alone stops exactly at the top of the bar, so the last card ends up glued to it —
 * the screen reads as cut off rather than finished. Which screens looked right was pure luck
 * until now: the ones with a trailing padding of their own had air, the rest had none
 * (`ShopItemsScreen` 0, `InventoryScreen` 0, `ReferralScreen` 0.25).
 *
 * 16 is measured, not chosen: it is the gap on `CaseDetailsScreen`, the screen named as the
 * reference, and the same 16 that `AchievementsScreen` and `ReferralContestScreen` happened to
 * land on. On the reference screen the balance pill sits flush with the bar's top edge, so the
 * distance to the pill and to the bar are one number, not two. */
const SCREEN_BOTTOM_GAP_PX = 16;

interface FixedElementState {
  content: ReactNode | null;
  title?: string;
  className?: string;
  height: number;
  /** Whether the blur/fade scrim renders behind this bar. A bar can be mounted
   * (reserving layout height) while rendering no visible content — e.g. an
   * `invisible` wrapper during a roulette spin — in which case the scrim must
   * stay off even though `content` is a truthy element. */
  showBlur: boolean;
  motion: FixedMotion;
}

interface TabBarState {
  visible: boolean;
  height: number;
}

interface ScreenLayoutContextValue {
  fixedTop: FixedElementState;
  fixedBottom: FixedElementState;
  tabBar: TabBarState;
  disableSafeArea: boolean;
  /** When true, screen is shown as preview during swipe-back - skip heavy operations */
  isPreview: boolean;
  /** When true, initial layout measurements are complete */
  layoutReady: boolean;
  setFixedTop: (content: ReactNode, title?: string, className?: string) => void;
  /** `animate` — the caller's motion preference (profile setting + OS
   * reduced-motion). The provider still vetoes it around navigation. */
  setFixedBottom: (content: ReactNode, className?: string, showBlur?: boolean, animate?: boolean) => void;
  clearFixedTop: () => void;
  clearFixedBottom: (animate?: boolean) => void;
  setTabBarVisible: (visible: boolean) => void;
  clearTabBar: () => void;
  registerTabBarOverride: () => void;
  unregisterTabBarOverride: () => void;
  setDisableSafeArea: (disabled: boolean) => void;
  registerFixedTopRef: (element: HTMLElement | null) => void;
  registerFixedBottomRef: (element: HTMLElement | null) => void;
  registerTabBarRef: (element: HTMLElement | null) => void;
}

const ScreenLayoutContext = createContext<ScreenLayoutContextValue | null>(null);

interface ScreenLayoutProviderProps {
  children: ReactNode;
  className?: string;
  /** When true, skips expensive ResizeObserver setup (used during swipe-back animation) */
  isPreview?: boolean;
}

// Internal component that subscribes to context and renders layout
const ScreenLayoutRenderer: FC<{ children: ReactNode; className?: string }> = ({ children, className }) => {
  const context = useContext(ScreenLayoutContext);
  if (!context) throw new Error('ScreenLayoutRenderer must be used within ScreenLayoutProvider');

  const { fixedTop, fixedBottom, tabBar, registerFixedTopRef, registerFixedBottomRef, registerTabBarRef } = context;
  const navigation = useNavigation();
  const { clearAll: clearAllScroll } = useScrollManager();
  const isDesktop = useIsDesktop();

  const handleTabChange = useCallback((tabId: TabId) => {
    clearAllScroll();
    navigation.changeTab(tabId);
  }, [navigation, clearAllScroll]);

  const handleActiveTabClick = useCallback((tabId: TabId) => {
    clearAllScroll();
    navigation.resetStack(tabId, tabId as ScreenName);
  }, [navigation, clearAllScroll]);

  // Use inline CSS variables scoped to this container.
  // Tabbar height collapses to 0 when bar is hidden so ScreenFixedBottom
  // and ScreenContent stop reserving space for a bar that isn't drawn.
  const layoutStyle = {
    '--screen-fixed-top-height': `${fixedTop.height}px`,
    '--screen-fixed-bottom-height': `${fixedBottom.height}px`,
    '--screen-tabbar-height': `${tabBar.visible ? tabBar.height : 0}px`,
    // Насколько панель поднята над низом окна. Единственный источник этого числа:
    // им позиционируется сама панель, по нему рисуется подложка и от него считает
    // резерв ScreenContent. Раньше выражение было переписано в каждом из трёх мест,
    // и резерв разошёлся с панелью — содержимое уезжало под неё.
    //
    // Без панели — ровно 0, а не «подъём, которого нет»: иначе экраны с таб-баром
    // и без панели (Главная, Кейсы, Профиль, Игры) получили бы лишние 8px внизу.
    '--screen-fixed-bottom-lift': fixedBottom.content
      ? 'max(calc(var(--screen-tabbar-height,0px) + min(8px, var(--screen-tabbar-height,0px))), calc(var(--tg-viewport-content-safe-area-inset-bottom,0px) + var(--tg-viewport-safe-area-inset-bottom,0px)))'
      : '0px',
    // Воздух между последним рядом содержимого и верхом панели. Тоже одной переменной и тоже
    // от наличия панели: зазор относится к панели, а не к таб-бару, и на экранах без панели
    // (Главная, Кейсы, Профиль, Игры) внизу не должно прибавиться ни пикселя.
    '--screen-fixed-bottom-gap': fixedBottom.content ? `${SCREEN_BOTTOM_GAP_PX}px` : '0px',
  } as React.CSSProperties;

  return (
    <div className={cn('absolute top-0 start-0 end-0 bottom-0', className)} style={layoutStyle}>
      {/* Blur + fade scrim behind the fixed-top header — mirror of the
          fixed-bottom scrim, inverted: opaque under the safe-area/header and
          feathering downward so scrolling content dissolves under it instead
          of hitting a hard edge. z-40 keeps it above scroll content but below
          the header itself (z-50), so the title/back button stay crisp. */}
      {(fixedTop.content || fixedTop.title) && !isDesktop && (
        <div
          aria-hidden
          className="pointer-events-none absolute inset-x-0 top-0 z-40"
          style={{
            height: 'calc(var(--screen-fixed-top-height,0px) + 24px)',
            backdropFilter: 'blur(20px)',
            WebkitBackdropFilter: 'blur(20px)',
            maskImage: 'linear-gradient(to bottom, #000 40%, transparent 100%)',
            WebkitMaskImage: 'linear-gradient(to bottom, #000 40%, transparent 100%)',
          }}
        />
      )}

      {(fixedTop.content || (fixedTop.title && !isDesktop)) && (
        <div
          ref={registerFixedTopRef}
          className={cn(
            'absolute top-0 start-0 end-0 z-50 min-h-[calc(var(--tg-viewport-content-safe-area-inset-top,0px)+var(--tg-viewport-safe-area-inset-top,0px))]',
            isDesktop && 'bg-transparent shadow-none',
            fixedTop.className
          )}
        >
          {fixedTop.title && !isDesktop && (
            <div className="mt-[var(--tg-viewport-safe-area-inset-top,0px)] h-[var(--tg-viewport-content-safe-area-inset-top,46px)] text-lg font-semibold leading-snug text-center m-0 flex items-center justify-center box-border">
              {fixedTop.title}
            </div>
          )}
          {fixedTop.content}
        </div>
      )}

      {children}

      {/* Blur + fade scrim behind the fixed-bottom bar — reaches from the very
          bottom of the screen up to the top of the bar, so scrolling content
          dissolves under it instead of hitting a hard edge. z-10 keeps it above
          the scroll content but below the tab bar (z-11) and the bar itself. */}
      {fixedBottom.content && fixedBottom.showBlur && (
        <div
          aria-hidden
          className={cn(
            'pointer-events-none absolute inset-x-0 bottom-0 z-10',
            fixedBottom.motion === 'in' && 'animate-shot-fixed-bottom-scrim-in',
            fixedBottom.motion === 'out' && 'animate-shot-fixed-bottom-scrim-out'
          )}
          style={{
            height: 'calc(var(--screen-fixed-bottom-height,0px) + var(--screen-fixed-bottom-lift,0px) + 48px)',
            backdropFilter: 'blur(20px)',
            WebkitBackdropFilter: 'blur(20px)',
            maskImage: 'linear-gradient(to top, #000 40%, transparent 100%)',
            WebkitMaskImage: 'linear-gradient(to top, #000 40%, transparent 100%)',
          }}
        />
      )}

      {fixedBottom.content && (
        <div
          ref={registerFixedBottomRef}
          className={cn(
            'absolute bottom-[var(--screen-fixed-bottom-lift,0px)] start-0 end-0 z-40',
            fixedBottom.motion === 'in' && 'animate-shot-fixed-bottom-in',
            // On the way out the bar is already conceptually gone — taps must
            // fall through to the screen underneath while it slides away.
            fixedBottom.motion === 'out' && 'animate-shot-fixed-bottom-out pointer-events-none',
            fixedBottom.className
          )}
        >
          {fixedBottom.content}
        </div>
      )}

      {tabBar.visible && !isDesktop && (
        <div ref={registerTabBarRef} className="absolute bottom-0 start-0 end-0 z-[11]">
          <TabBar
            activeTab={navigation.state.activeTab}
            onTabChange={handleTabChange}
            onActiveTabClick={handleActiveTabClick}
          />
        </div>
      )}
    </div>
  );
};

export const ScreenLayoutProvider: FC<ScreenLayoutProviderProps> = ({ children, className, isPreview = false }) => {
  const [fixedTop, setFixedTopState] = useState<FixedElementState>({
    content: null,
    className: undefined,
    height: 0,
    showBlur: true,
    motion: 'none',
  });

  const [fixedBottom, setFixedBottomState] = useState<FixedElementState>({
    content: null,
    className: undefined,
    height: 0,
    showBlur: true,
    motion: 'none',
  });

  // Navigation gate for the fixed-bottom entrance/exit. The screen element is
  // this provider's `children`; when it swaps, the bar's mount/unmount that
  // follows belongs to the navigation, not to a mode the user turned on. The
  // initial value marks mount time, so the first bar a screen registers never
  // animates in. Written during render on purpose — the state updates it has to
  // beat are scheduled from the child's layout effect of that very commit.
  const screenChangedAtRef = useRef(0);
  const screenChildrenRef = useRef<ReactNode>(undefined);
  if (screenChildrenRef.current !== children) {
    screenChildrenRef.current = children;
    screenChangedAtRef.current = typeof performance !== 'undefined' ? performance.now() : Date.now();
  }
  const exitTimerRef = useRef<number | null>(null);
  const isNavigating = useCallback(() => {
    const now = typeof performance !== 'undefined' ? performance.now() : Date.now();
    return now - screenChangedAtRef.current < SCREEN_SETTLE_MS;
  }, []);

  const pathname = usePathname();
  const runtime = useRuntime();
  // Tab bar height tracked independently of visibility.
  const [tabBarHeight, setTabBarHeight] = useState(0);
  // Number of mounted <ScreenTabBar /> instances. Non-tab-root screens
  // that want the bar (e.g. /help/recruiter) opt in via this counter.
  const [tabBarOverrideCount, setTabBarOverrideCount] = useState(0);
  // Imperative manual visibility. `null` = derive from pathname/override,
  // boolean = explicit force. Retained for legacy callers; consumers
  // should prefer the override register/unregister API.
  const [tabBarManualVisible, setTabBarManualVisible] = useState<boolean | null>(null);

  // Pathname-based detection only valid in web runtime — TMA keeps a
  // static `/` URL across stack navigation, so pathname would falsely
  // show the bar on every screen. TMA screens opt in via <ScreenTabBar/>.
  const tabBarVisible =
    tabBarManualVisible !== null
      ? tabBarManualVisible
      : tabBarOverrideCount > 0 || (runtime === 'web' && isTabRootPathname(pathname));
  const tabBar: TabBarState = { visible: tabBarVisible, height: tabBarHeight };

  const [disableSafeArea, setDisableSafeArea] = useState(false);
  const [layoutReady, setLayoutReady] = useState(false);

  const fixedTopRef = useRef<HTMLElement | null>(null);
  const fixedBottomRef = useRef<HTMLElement | null>(null);
  const tabBarRef = useRef<HTMLElement | null>(null);
  const resizeObserverRef = useRef<ResizeObserver | null>(null);
  const rafIdRef = useRef<number | null>(null);

  // Setup ResizeObserver - in preview mode, do one-time measurement without observers
  useLayoutEffect(() => {
    // In preview mode, do a single synchronous measurement without ResizeObserver
    if (isPreview) {
      if (fixedTopRef.current) {
        const height = fixedTopRef.current.offsetHeight;
        if (height > 0) {
          setFixedTopState((prev) => (prev.height !== height ? { ...prev, height } : prev));
        }
      }
      if (fixedBottomRef.current) {
        const height = fixedBottomRef.current.offsetHeight;
        if (height > 0) {
          setFixedBottomState((prev) => (prev.height !== height ? { ...prev, height } : prev));
        }
      }
      if (tabBarRef.current) {
        const height = tabBarRef.current.offsetHeight;
        if (height > 0) {
          setTabBarHeight((prev) => (prev !== height ? height : prev));
        }
      }
      setLayoutReady(true);
      return;
    }

    const observeElement = (element: HTMLElement | null, type: 'top' | 'bottom') => {
      if (!element) return;
      const height = element.offsetHeight;
      if (type === 'top') {
        setFixedTopState((prev) => (prev.height !== height ? { ...prev, height } : prev));
      } else {
        setFixedBottomState((prev) => (prev.height !== height ? { ...prev, height } : prev));
      }
      resizeObserverRef.current?.observe(element);
    };

    resizeObserverRef.current = new ResizeObserver((entries) => {
      for (const entry of entries) {
        if (entry.target === fixedTopRef.current) {
          if (rafIdRef.current) {
            cancelAnimationFrame(rafIdRef.current);
          }
          rafIdRef.current = requestAnimationFrame(() => {
            const height = entry.target.getBoundingClientRect().height;
            setFixedTopState((prev) => (prev.height !== height ? { ...prev, height } : prev));
          });
        } else if (entry.target === fixedBottomRef.current) {
          if (rafIdRef.current) {
            cancelAnimationFrame(rafIdRef.current);
          }
          rafIdRef.current = requestAnimationFrame(() => {
            const height = entry.target.getBoundingClientRect().height;
            setFixedBottomState((prev) => (prev.height !== height ? { ...prev, height } : prev));
          });
        } else if (entry.target === tabBarRef.current) {
          if (rafIdRef.current) {
            cancelAnimationFrame(rafIdRef.current);
          }
          rafIdRef.current = requestAnimationFrame(() => {
            const height = entry.target.getBoundingClientRect().height;
            setTabBarHeight((prev) => (prev !== height ? height : prev));
          });
        }
      }
    });

    if (fixedTopRef.current) {
      const height = fixedTopRef.current.offsetHeight;
      setFixedTopState((prev) => (prev.height !== height ? { ...prev, height } : prev));
      observeElement(fixedTopRef.current, 'top');
    }
    if (fixedBottomRef.current) {
      observeElement(fixedBottomRef.current, 'bottom');
    }
    if (tabBarRef.current) {
      const height = tabBarRef.current.offsetHeight;
      setTabBarHeight((prev) => (prev !== height ? height : prev));
      resizeObserverRef.current?.observe(tabBarRef.current);
    }

    setLayoutReady(true);

    return () => {
      if (rafIdRef.current) {
        cancelAnimationFrame(rafIdRef.current);
      }
      resizeObserverRef.current?.disconnect();
    };
  }, [isPreview, fixedTop.content, fixedTop.title, fixedBottom.content, tabBarVisible]);

  // Ensure TabBar height is measured when needed
  useLayoutEffect(() => {
    if (isPreview) return;
    if (tabBarVisible && tabBarHeight === 0 && tabBarRef.current) {
      const height = tabBarRef.current.offsetHeight;
      if (height > 0) {
        setTabBarHeight(height);
      }
    }
  }, [isPreview, tabBarVisible, tabBarHeight]);

  // Ensure FixedTop height is measured when needed
  useLayoutEffect(() => {
    if (isPreview) return;
    if ((fixedTop.content || fixedTop.title) && fixedTop.height === 0 && fixedTopRef.current) {
      const height = fixedTopRef.current.offsetHeight;
      if (height > 0) {
        setFixedTopState((prev) => ({ ...prev, height }));
      }
    }
  }, [isPreview, fixedTop.content, fixedTop.title, fixedTop.height]);

  const setFixedTop = useCallback((content: ReactNode, title?: string, className?: string) => {
    // preserve the measured height on a content swap (see setFixedBottom) to avoid
    // collapsing the top padding for a frame
    setFixedTopState((prev) => ({ content, title, className, height: prev.height, showBlur: true, motion: 'none' }));
  }, []);

  const setFixedBottom = useCallback((content: ReactNode, className?: string, showBlur = true, animate = false) => {
    // A bar arriving cancels a pending exit — this is also how a screen swap
    // (old bar unmounts, new one mounts in the same commit) collapses back into
    // a plain content swap instead of an exit followed by an entrance.
    if (exitTimerRef.current !== null) {
      window.clearTimeout(exitTimerRef.current);
      exitTimerRef.current = null;
    }
    const withMotion = animate && !isPreview && !isNavigating();
    // Keep the previously measured height — a content swap (e.g. a screen
    // re-render handing a new children ref) must NOT collapse the height var to 0,
    // which would shrink ScreenContent's bottom padding and jump the scroll. The
    // ResizeObserver still corrects the height if it actually changed.
    setFixedBottomState((prev) => {
      // Only a bar that wasn't there (or was on its way out) plays an entrance.
      // Swapping the buttons inside a bar that already stands must not replay it.
      const appearing = prev.content == null || prev.motion === 'out';
      const motion: FixedMotion = appearing ? (withMotion ? 'in' : 'none') : prev.motion;
      return { content, className, height: prev.height, showBlur, motion };
    });
  }, [isPreview, isNavigating]);

  const clearFixedTop = useCallback(() => {
    setFixedTopState({ content: null, title: undefined, className: undefined, height: 0, showBlur: true, motion: 'none' });
  }, []);

  const dropFixedBottom = useCallback(() => {
    setFixedBottomState({ content: null, className: undefined, height: 0, showBlur: true, motion: 'none' });
  }, []);

  const clearFixedBottom = useCallback((animate = false) => {
    // A second clear must not leave the first timer running — it would fire late
    // and drop a bar that has since been set again.
    if (exitTimerRef.current !== null) {
      window.clearTimeout(exitTimerRef.current);
      exitTimerRef.current = null;
    }
    if (!animate || isPreview || isNavigating()) {
      dropFixedBottom();
      return;
    }
    // Two-phase teardown, same shape as BottomSheet: keep the bar mounted with
    // the exit classes on, drop it once the slide-down has actually played.
    // Height stays put for that stretch too, so ScreenContent's bottom padding
    // collapses when the bar is gone rather than under a bar still on screen.
    setFixedBottomState((prev) => (prev.content == null ? prev : { ...prev, motion: 'out' }));
    exitTimerRef.current = window.setTimeout(() => {
      exitTimerRef.current = null;
      dropFixedBottom();
    }, FIXED_BOTTOM_EXIT_MS);
  }, [dropFixedBottom, isPreview, isNavigating]);

  useEffect(() => () => {
    if (exitTimerRef.current !== null) window.clearTimeout(exitTimerRef.current);
  }, []);

  const registerFixedTopRef = useCallback((element: HTMLElement | null) => {
    fixedTopRef.current = element;
  }, []);

  const registerFixedBottomRef = useCallback((element: HTMLElement | null) => {
    fixedBottomRef.current = element;
  }, []);

  const setTabBarVisible = useCallback((visible: boolean) => {
    setTabBarManualVisible(visible);
  }, []);

  const clearTabBar = useCallback(() => {
    setTabBarManualVisible(null);
    setTabBarHeight(0);
  }, []);

  const registerTabBarOverride = useCallback(() => {
    setTabBarOverrideCount((c) => c + 1);
  }, []);

  const unregisterTabBarOverride = useCallback(() => {
    setTabBarOverrideCount((c) => Math.max(0, c - 1));
  }, []);

  const registerTabBarRef = useCallback((element: HTMLElement | null) => {
    tabBarRef.current = element;
  }, []);

  const value: ScreenLayoutContextValue = {
    fixedTop,
    fixedBottom,
    tabBar,
    disableSafeArea,
    isPreview,
    layoutReady,
    setFixedTop,
    setFixedBottom,
    clearFixedTop,
    clearFixedBottom,
    setTabBarVisible,
    clearTabBar,
    registerTabBarOverride,
    unregisterTabBarOverride,
    setDisableSafeArea,
    registerFixedTopRef,
    registerFixedBottomRef,
    registerTabBarRef,
  };

  return (
    <ScreenLayoutContext.Provider value={value}>
      <ScreenLayoutRenderer className={className}>{children}</ScreenLayoutRenderer>
    </ScreenLayoutContext.Provider>
  );
};

export { ScreenLayoutContext };
